/** New game: map size, faction, opponents, difficulty and the seed.
 *
 * The faction picker draws each town's own painting at an exact eighth
 * (1280x720 -> 160x90), which is the whole point of having twelve town screens
 * in the atlas: the player chooses a faction by looking at it, not by reading
 * its name off a dropdown.
 *
 * The seed is a first-class field rather than a debug affordance. Every roll in
 * the kernel comes from a named stream threaded through state (I5), so two
 * players who type the same seed get the same world, and a bug report that
 * carries a seed is reproducible.
 */

import { useMemo, useState } from "react";
import type { ContentRegistry, FactionDef, Resources } from "@game/core";
import { RESOURCES, STARTING_RESOURCES } from "@game/core";
import { buildingIcon, townBackgroundKey } from "./art.js";
import { ScreenBackdrop } from "./MainMenu.js";
import { resolveTownSlots } from "./townSlots.js";
import { groupDigits, titleCase } from "./format.js";
import { usePolishStyles } from "./polish.js";
import { MaybeIcon, ScreenStyles } from "./primitives.js";
import { usePhoneLayout } from "../theme/phone.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

export interface MapSizeOption {
  readonly id: string;
  readonly label: string;
  readonly cells: number;
}

/**
 * Four sizes, one side of the square map each.
 *
 * Small is 48 rather than the classic's own 36. That is measured, not a preference:
 * on 36x36 with two players, five games in ten are decided inside ten days
 * (4 5 6 7 9 14 25 25 201 201) because even a separation that satisfies the
 * generator's floor is under two days' march, so one side's opening army
 * arrives before there is a game to play. The identical code at 48x48 produces
 * 11 12 22 23 25 30 32 41 148 201 with nothing under ten days. The generator
 * enforces the same floor in MIN_MAP_CELLS; this list must not offer a size it
 * would refuse to build.
 */
export const MAP_SIZES: readonly MapSizeOption[] = Object.freeze([
  { id: "S", label: "Small", cells: 48 },
  { id: "M", label: "Medium", cells: 72 },
  { id: "L", label: "Large", cells: 108 },
  { id: "XL", label: "Extra Large", cells: 144 },
  { id: "XXL", label: "Massive", cells: 288 },
]);

export const DIFFICULTIES: readonly string[] =
  Object.freeze(["Easy", "Normal", "Hard", "Impossible"]);

/**
 * What a size buys, in one line.
 *
 * Arithmetic and one measured fact. The ground is `cells * cells`; the ratio is
 * against Small. The claim about the floor is the sample recorded on
 * `MAP_SIZES` - ten generated games each at 36 and 48 cells - and not a feeling
 * about map sizes, because a setup screen that promises a kind of game is
 * promising something the generator has to deliver.
 */
export function mapSizeNote(id: string): string {
  const m = MAP_SIZES.find((x) => x.id === id) ?? MAP_SIZES[0];
  if (m === undefined) return "";
  const small = MAP_SIZES[0]?.cells ?? 48;
  const tiles = m.cells * m.cells;
  const ground = `${groupDigits(tiles)} tiles of ground`;
  if (m.cells === small) {
    return `${ground}, and the least the generator will build on: below 48 cells `
      + "the two starts are inside two days' march and the game is decided in the opening.";
  }
  const ratio = tiles / (small * small);
  return `${ground} - ${ratio.toFixed(1)} times a small map. Longer marches, more room to `
    + "expand into, and a longer game.";
}

/**
 * The purse each level starts the HUMAN with, in the screen's own order.
 *
 * Read off the kernel's table rather than restated here, because the number the
 * screen promises and the number `newGame` hands over have to be the same
 * number. Difficulty in the classic is the player's handicap: every computer opponent
 * begins on the Normal row whatever this is set to, and what the level buys
 * them instead is a deeper search (`packages/ai/src/difficulty.ts`).
 */
export const DIFFICULTY_PURSES: readonly Resources[] = Object.freeze([
  STARTING_RESOURCES.easy, STARTING_RESOURCES.normal,
  STARTING_RESOURCES.hard, STARTING_RESOURCES.impossible,
]);

/** What the computer does with the level, in one line per level. */
export const DIFFICULTY_NOTES: readonly string[] = Object.freeze([
  "The computer plans one day at a time, fights without probing, and keeps two heroes.",
  "The computer plans several days ahead, probes the fights it picks, and keeps three heroes.",
  "The computer searches wider, marches on towns a week away, and defends more ground.",
  "The computer searches deepest and commits on the thinnest margin it has measured. "
    + "The handicap is yours alone: it still starts on the Normal purse.",
]);

/**
 * A win condition a player may choose, and the sentence that explains it.
 *
 * `id` is the kernel's own condition string (`state.victory.condition`,
 * `conditionWinner` in adventure/turn.ts). A condition that takes a number is
 * listed WITHOUT one and carries its choices in `amounts`; the screen joins the
 * two with a colon, which is the encoding `newGame` reads.
 *
 * Three, not the six kinds the kernel parses, and the reason is stated here
 * because a chooser that offers a condition the game cannot honour is worse
 * than one that offers fewer:
 *
 *  - `capture_town` awards the game to whoever OWNS the named town, so pointed
 *    at any town on a generated map it is a win on turn one for that town's
 *    owner. `resolveVictoryCondition` refuses it for exactly that reason. It
 *    becomes offerable the day a map can start with a town nobody holds.
 *  - `defeat_hero` and `survive` both also require their chooser to be the last
 *    player standing, which the kernel already ends the game on by itself, so
 *    neither changes a single game. Offering them would be three names for one
 *    condition.
 *
 * The other two are real: each ends a game the survivor count would not have
 * ended, and each still lets the last kingdom standing win the ordinary way.
 */
export interface VictoryOption {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  /** Offered when the kernel's kind takes a number. */
  readonly amounts?: readonly number[];
}

export const VICTORY_OPTIONS: readonly VictoryOption[] = Object.freeze([
  {
    id: "defeat_all",
    label: "Defeat every rival",
    description: "The ordinary war. You win when no rival kingdom is left. "
      + "A player who loses his last town has seven days to take another.",
  },
  {
    id: "accumulate_gold",
    label: "Amass a fortune",
    amounts: Object.freeze([50000, 100000, 250000]),
    description: "The first kingdom whose treasury reaches the sum wins outright, "
      + "however much of the map anyone holds. Being the last one standing still wins too.",
  },
  {
    id: "acquire_artifact",
    label: "Seize the great artifact",
    description: "One artifact decides the war: carry it and you win. The map names "
      + "the costliest relic it hid, wherever it hid it. Being the last one standing still wins too.",
  },
]);

/**
 * The purse in a sentence.
 *
 * Every row of `STARTING_RESOURCES` gives wood and ore the same number and the
 * four rare resources another, so one of each is enough to describe it; the
 * all-zero row gets the sentence that is the whole point of the level.
 */
function purseText(r: Resources | undefined): string {
  if (r === undefined) return "the standard purse";
  if (RESOURCES.every((k) => r[k] === 0)) return "nothing at all";
  return `${groupDigits(r.gold)} gold, ${String(r.wood)} wood and ore, `
    + `and ${String(r.mercury)} of each rare resource`;
}

/** `"accumulate_gold"` + 100000 -> `"accumulate_gold:100000"`. */
export function victoryCondition(option: VictoryOption, amount: number): string {
  return option.amounts === undefined ? option.id : `${option.id}:${String(amount)}`;
}

export interface NewGameSetup {
  readonly mapSize: string;
  readonly mapCells: number;
  readonly faction: string;
  readonly opponents: number;
  /**
   * How many kingdoms are people at this machine. 1 is the ordinary game.
   *
   * Above 1 is hotseat, and it is bounded by the number of kingdoms rather
   * than offered freely: "three people in a two-kingdom game" is not a setup,
   * it is a typo.
   */
  readonly humans: number;
  readonly difficulty: number;
  /** Unsigned 32-bit; the kernel's `rootSeed`. */
  readonly seed: number;
  /** The kernel's `NewGameSetup.victory`, already encoded. */
  readonly victory: string;
}

export interface NewGameScreenProps {
  readonly content: ContentRegistry;
  readonly initial?: Partial<NewGameSetup>;
  readonly onStart: (setup: NewGameSetup) => void;
  readonly onBack: () => void;
}

const SEED_MAX = 0xffffffff;

/** Factions a player may pick: the twelve towns, never the roster-less neutral. */
function playableFactions(content: ContentRegistry): readonly FactionDef[] {
  const out: FactionDef[] = [];
  for (const id of [...content.factions.keys()].sort()) {
    const def = content.factions.get(id);
    if (def === undefined || def.creatures.length === 0 || def.buildings.length === 0) continue;
    out.push(def);
  }
  return out;
}

export function NewGameScreen(props: NewGameScreenProps): JSX.Element {
  usePolishStyles();
  /**
   * A phone gets its own layout, not a squeezed copy of the desktop one.
   *
   * The faction picker is six fixed 200px columns - 1200px before gaps - so on
   * a 390 or 844 wide screen it overflowed BOTH edges and centred, which put
   * five of the fourteen factions off the sides where nothing could reach
   * them. Everything under it (map size, opponents, difficulty, seed, and the
   * button that starts the game) then began below the fold of a 390-tall
   * screen that did not scroll: measured `scrollHeight === clientHeight`, so
   * the page was not long, it was CUT. A player on a phone could pick one of
   * nine factions and could not start a game at all.
   */
  const phone = usePhoneLayout();
  const factions = useMemo(() => playableFactions(props.content), [props.content]);
  const first = factions[0]?.id as string | undefined;

  const [mapSize, setMapSize] = useState(props.initial?.mapSize ?? "M");
  const [faction, setFaction] = useState(props.initial?.faction ?? first ?? "sunspire");
  const [opponents, setOpponents] = useState(props.initial?.opponents ?? 1);
  const [humans, setHumans] = useState(props.initial?.humans ?? 1);
  const [difficulty, setDifficulty] = useState(props.initial?.difficulty ?? 1);
  const [seedText, setSeedText] = useState(String(props.initial?.seed ?? 1));
  const [victoryId, setVictoryId] = useState(
    () => initialVictoryId(props.initial?.victory),
  );
  const [goldTarget, setGoldTarget] = useState(
    () => initialVictoryAmount(props.initial?.victory),
  );

  const size = MAP_SIZES.find((m) => m.id === mapSize) ?? MAP_SIZES[1] ?? MAP_SIZES[0];
  const kingdoms = opponents + 1;
  // Clamped for display as well as on start: dropping the opponent count under
  // the seat count must not leave a button lit that no longer means anything.
  const seats = humans > kingdoms ? kingdoms : humans;
  const seed = parseSeed(seedText);
  const victoryOption = VICTORY_OPTIONS.find((v) => v.id === victoryId) ?? VICTORY_OPTIONS[0]!;
  /* What the Begin plate is about to do, in one line, next to the title. The
     top bar of this screen carried a five-word heading and 1,700px of empty
     plank at 1920. */
  const summary = `${titleCase(faction)} - ${size?.label ?? ""} map - `
    + `${String(opponents)} ${opponents === 1 ? "rival" : "rivals"} - `
    + `${DIFFICULTIES[difficulty] ?? ""}`;

  const start = (): void => {
    if (size === undefined) return;
    props.onStart({
      mapSize: size.id,
      mapCells: size.cells,
      faction,
      opponents,
      humans: seats,
      difficulty,
      seed,
      victory: victoryCondition(victoryOption, goldTarget),
    });
  };

  return (
    <div
      className={`hs-screen hm-ng ${phone.phone ? "hm-ng--phone" : ""}`}
      role="region"
      aria-label="New game"
    >
      <ScreenStyles />
      <SetupStyles />
      {/* The faction the player is standing on. It changes as the choice
          changes, which is the one thing a picker of paintings can do that a
          picker of names cannot. */}
      <ScreenBackdrop faction={faction} />
      {/* A tooth over the painting.
          The backdrop is a 1280x720 town screen magnified to cover, graded down
          to 0.46 brightness, with a smooth two-part wash over it - and a smooth
          wash is the one surface that measures as empty however good it looks.
          On the 1920x1080 shot the 192px margin down the left of this screen,
          the 224px down the right and the 150 rows under the last card were all
          flat by a 32x32 luminance-deviation scan: 32.9% of the frame, on a
          screen whose whole argument is that you choose a faction by looking at
          a picture. This is the canvas the picture is painted on, not a filter
          on the picture: it is a separate layer above the backdrop and below the
          panels, so nothing a player reads is textured by it. */}
      <span className="hm-ng__grain" aria-hidden="true" />

      <div className="hs-screen__bar hs-plank">
        <span className="hs-screen__title hm-cap">New Game</span>
        <span className="hs-screen__sub hs-muted">{summary}</span>
        <span className="hs-spacer" />
      </div>

      <div
        className="hs-screen__body"
        style={phone.phone
          // The phone body IS the scroller. Centring a column taller than the
          // frame pins its top above the top edge, and `safe center` only saves
          // the cases the browser can see - it cannot scroll a container whose
          // overflow is hidden. Start-aligned and scrolling is the honest shape.
          ? { justifyContent: "flex-start", alignItems: "stretch", padding: "8px", overflowY: "auto" }
          : { justifyContent: "center", padding: "14px" }}
      >
        <div className="hs-setup hs-scroll">
          <div className="hs-card hs-wood">
            <div className="hs-field__label" style={{ marginBottom: "6px" }}>Faction</div>
            <div className="hs-factiongrid">
              {factions.map((f) => (
                <button
                  key={f.id as string}
                  type="button"
                  className={`hs-faction ${(f.id as string) === faction ? "hs-faction--on" : ""}`}
                  aria-pressed={(f.id as string) === faction}
                  title={`${f.name} — ${titleCase(f.nativeTerrain)} · ${f.mightClass} / ${f.magicClass}`}
                  onClick={() => setFaction(f.id as string)}
                >
                  <span className="hs-faction__scene">
                    {/* 250, which is the widest tile any breakpoint draws.
                        The sprite is fitted to this box and centred in a tile
                        with overflow hidden, so a painting NARROWER than its
                        tile leaves two black bars - the one thing a picker of
                        paintings must not do - while a wider one is simply
                        cropped, which is what a window onto a landscape is.
                        At 200 the roomy breakpoint's 250px tile framed a 200px
                        painting in 25px of black down each side. */}
                    <MaybeIcon
                      atlasKey={townBackgroundKey(f.id as string)}
                      size={250}
                      className="hs-faction__bg"
                    />
                    <MaybeIcon
                      atlasKey={keepIcon(props.content, f)}
                      size={140}
                      className="hs-faction__crest"
                      title={f.name}
                    />
                  </span>
                  <span className="hs-faction__cap hs-clip">{f.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="hs-setup__grid">
            <div className="hs-card hs-wood">
              <div className="hs-field">
                <span className="hs-field__label">Map size</span>
                <div className="hs-choices">
                  {MAP_SIZES.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className={`hs-choice ${m.id === mapSize ? "hs-choice--on" : ""}`}
                      aria-pressed={m.id === mapSize}
                      onClick={() => setMapSize(m.id)}
                    >
                      {m.label}
                      <span className="hs-choice__sub">{`${String(m.cells)} x ${String(m.cells)}`}</span>
                    </button>
                  ))}
                </div>
                {/* Every other card on this screen says what its choice buys,
                    and this one said nothing - so in a row with Opponents it
                    was 74px of stretched empty card under four buttons, which
                    a blank-surface scan counts and the eye reads as a panel
                    that ran out. The sentence is arithmetic and one measured
                    fact, not a mood: see MAP_SIZES for the ten-game sample the
                    48-cell floor came out of. */}
                <span className="hs-tiny hs-muted" style={{ marginTop: "6px" }}>
                  {mapSizeNote(mapSize)}
                </span>
              </div>
            </div>

            <div className="hs-card hs-wood">
              <div className="hs-field">
                <span className="hs-field__label">Opponents</span>
                <div className="hs-choices">
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`hs-choice ${n === opponents ? "hs-choice--on" : ""}`}
                      aria-pressed={n === opponents}
                      style={{ minWidth: "44px" }}
                      onClick={() => setOpponents(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div className="hs-field" style={{ marginTop: "10px" }}>
                <span className="hs-field__label">People at this machine</span>
                <div className="hs-choices">
                  {Array.from({ length: kingdoms }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`hs-choice ${n === seats ? "hs-choice--on" : ""}`}
                      // The opponent row above offers the same digits, so a bare
                      // "1" names two controls on this screen - unreadable to a
                      // screen reader and ambiguous to any locator that asks by
                      // name. The seat count says what it counts.
                      aria-label={`${String(n)} ${n === 1 ? "person" : "people"} at this machine`}
                      aria-pressed={n === seats}
                      style={{ minWidth: "44px" }}
                      onClick={() => { setHumans(n); }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <span className="hs-tiny hs-muted" style={{ marginTop: "6px" }}>
                  {seats === 1
                    ? `You against ${String(opponents)} computer ${opponents === 1 ? "kingdom" : "kingdoms"}.`
                    : `${String(seats)} of the ${String(kingdoms)} kingdoms are people taking turns `
                      + `at this machine; the other ${String(kingdoms - seats)} `
                      + `${kingdoms - seats === 1 ? "is a computer" : "are computers"}. `
                      + "The game covers the screen between turns so nobody sees anybody else's map."}
                </span>
              </div>
            </div>

            <div className="hs-card hs-wood">
              <div className="hs-field">
                <span className="hs-field__label">Difficulty</span>
                <div className="hs-choices">
                  {DIFFICULTIES.map((d, i) => (
                    <button
                      key={d}
                      type="button"
                      className={`hs-choice ${i === difficulty ? "hs-choice--on" : ""}`}
                      aria-pressed={i === difficulty}
                      onClick={() => setDifficulty(i)}
                    >
                      {d}
                    </button>
                  ))}
                </div>
                <span className="hs-tiny hs-muted" style={{ marginTop: "6px" }}>
                  You begin with {purseText(DIFFICULTY_PURSES[difficulty])}.{" "}
                  {DIFFICULTY_NOTES[difficulty] ?? ""}
                </span>
              </div>
            </div>

            <div className="hs-card hs-wood">
              <div className="hs-field">
                <span className="hs-field__label">Seed</span>
                <div className="hs-choices">
                  <input
                    className="hs-input"
                    inputMode="numeric"
                    value={seedText}
                    aria-label="Map seed"
                    onChange={(e) => setSeedText(e.target.value)}
                  />
                  <button
                    type="button"
                    className="hs-plate hs-plate--sm"
                    title="Pick a new seed"
                    onClick={() => setSeedText(String(randomSeed()))}
                  >
                    Reroll
                  </button>
                </div>
                <span className="hs-tiny hs-muted">
                  {`Every world is a function of this number: ${String(seed)}. Two players who type the same seed get the same map.`}
                </span>
              </div>
            </div>
            <div className="hs-card hs-wood hm-ng__victory">
              <div className="hs-field">
                <span className="hs-field__label">Victory condition</span>
                <div className="hs-choices">
                  {VICTORY_OPTIONS.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className={`hs-choice ${v.id === victoryId ? "hs-choice--on" : ""}`}
                      aria-pressed={v.id === victoryId}
                      onClick={() => setVictoryId(v.id)}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
                {victoryOption.amounts !== undefined && (
                  <div className="hs-choices" style={{ marginTop: "6px" }}>
                    {victoryOption.amounts.map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`hs-choice ${n === goldTarget ? "hs-choice--on" : ""}`}
                        aria-pressed={n === goldTarget}
                        onClick={() => setGoldTarget(n)}
                      >
                        {`${groupDigits(n)} gold`}
                      </button>
                    ))}
                  </div>
                )}
                <span className="hs-tiny hs-muted">{victoryOption.description}</span>
              </div>
            </div>

          </div>

          {/* BEGIN and BACK where the reference keeps them: two large gold
              plates at the bottom-right of the leather panel
              (expansion-gallery_hc_scr_4: ~200x40 at 800x600). They were two
              85x24 chips in the header, which is a web form's OK button. */}
          <div className="hm-ng__plates">
            <button type="button" className="hs-plate hm-ng__plate hm-ng__plate--begin" onClick={start}>
              <span className="hm-ng__plategold">BEGIN</span>
            </button>
            <button type="button" className="hs-plate hm-ng__plate" onClick={props.onBack}>
              <span className="hm-ng__plategold">BACK</span>
            </button>
          </div>
          <div className="hs-card hs-parch hm-ng__note">
            <div className="hs-card__title">Starting out</div>
            <div className="hs-tiny hs-muted">
              {factionBlurb(props.content, faction)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The keep that stands for a faction: the top step of its fort chain.
 *
 * Resolved through the same slot machinery the town screen uses, with every one
 * of the faction's buildings treated as built - so the thumbnail shows the
 * Castle rather than the Fort, and a faction whose chain is authored
 * differently still resolves to whatever its deepest step is.
 */
function keepIcon(content: ContentRegistry, faction: FactionDef): string | null {
  const fort = resolveTownSlots(content, faction.id as string, faction.buildings)
    .find((s) => s.slot.name === "fort");
  const def = fort?.built ?? fort?.chain[fort.chain.length - 1] ?? null;
  return def === null || def === undefined ? null : buildingIcon(def.art);
}

/** Clamp whatever was typed into an unsigned 32-bit seed, without throwing. */
export function parseSeed(text: string): number {
  const digits = text.replace(/[^0-9]/g, "");
  if (digits === "") return 1;
  const n = Number(digits);
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(SEED_MAX, Math.trunc(n)));
}

/** UI-side only. The kernel never rolls anything outside a named stream (I5). */
const randomSeed = (): number => Math.floor(Math.random() * SEED_MAX) + 1;

function factionBlurb(content: ContentRegistry, id: string): string {
  const def = content.factions.get(id as FactionDef["id"]);
  if (def === undefined) return "";
  return `${def.name}: ${def.mightClass} and ${def.magicClass} heroes, native to ${titleCase(def.nativeTerrain)}. `
    + `${String(def.creatures.length)} creature forms, ${String(def.buildings.length)} structures.`;
}

/**
 * Which option an already-encoded condition came from.
 *
 * `initial` carries the kernel's string, not the screen's option, so the two
 * are pulled apart here rather than being kept as a second field that could
 * disagree with it.
 */
export function initialVictoryId(condition: string | undefined): string {
  if (condition === undefined || condition === "") return VICTORY_OPTIONS[0]!.id;
  const sep = condition.indexOf(":");
  const kind = sep < 0 ? condition : condition.slice(0, sep);
  return VICTORY_OPTIONS.some((v) => v.id === kind) ? kind : VICTORY_OPTIONS[0]!.id;
}

/** The number in an encoded condition, or the middle of the offered amounts. */
export function initialVictoryAmount(condition: string | undefined): number {
  const fallback = VICTORY_OPTIONS.find((v) => v.amounts !== undefined)?.amounts?.[1] ?? 100000;
  if (condition === undefined) return fallback;
  const sep = condition.indexOf(":");
  if (sep < 0) return fallback;
  const n = Number.parseInt(condition.slice(sep + 1), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

// ------------------------------------------------------------------ styles

const SETUP_STYLE_ID = "hm-newgame-art";

/* Mounted after `styles.ts` and after `polish.ts`, so an equal-specificity rule
   here wins on document order alone.
   NOTE: this is a template literal. No backticks below, in a comment or
   anywhere else - one ends the string and the rest of the file is parsed as
   TypeScript, and the error lands on a line of English. */
export const SETUP_CSS = `
/* ---- the frame, at the size the frame actually is.
   The setup column was min(940px, 94vw) at every width, so a 1920x1080 screen
   spent 980px - 51% of its width - and the bottom 160px on nothing at all, and
   the two-column grid inside it left half of the Difficulty and Seed rows
   empty. Three columns from 1500px up puts every card beside another one and
   ends the ragged right edge. */
.hm-ng__grain {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background-image:
    repeating-linear-gradient(118deg,
      rgba(255,226,182,0.05) 0 2px,
      rgba(0,0,0,0.10) 2px 7px),
    repeating-linear-gradient(28deg,
      rgba(255,232,190,0.035) 0 1px,
      rgba(0,0,0,0.07) 1px 11px);
}

.hm-ng .hs-setup { width: min(1180px, 94vw); }
@media (min-width: 1700px) {
  .hm-ng .hs-setup { width: min(1560px, 92vw); }
}

/* ---- the column has to be a scroller, not a tall thing in a clipped box.
   At 1366x768 this screen's stack is taller than the frame, and what it did
   about that was nothing: '.hs-screen__body' is 'overflow: hidden', the column
   is a flex item whose height is its own content, so 'hs-scroll''s
   'overflow-y: auto' had no overflow of its OWN to act on. The DIFFICULTY note
   was sliced through the middle of a line at the bottom edge and Victory
   Condition, Seed and Starting Out were not reachable at all - a gilt-pixel
   scan of the column's right edge found the panel border and no thumb, because
   there was no scrollbar to paint.
   'max-height: 100%' is the whole fix: it caps the item at the body's height,
   which gives the scroller something to scroll. The gutter is reserved so the
   cards do not shift by 10px the moment the list gets long enough to need one. */
.hm-ng .hs-setup {
  max-height: 100%;
  scrollbar-gutter: stable;
  padding-right: 2px;
}
/* ...and the cut has to be VISIBLE, which the browser will not do for us.
   The kit's own '.hs-scroll' asks for a gilt thumb through
   '::-webkit-scrollbar-thumb', and in this build's own capture environment
   nothing is painted into it: with the element scrolling 168px, offsetWidth
   1180 against clientWidth 1170 says ten pixels of gutter are reserved, and
   every pixel in that gutter samples as the backdrop behind the panel. Four
   variants were tried against the live page - as-is, 'scrollbar-width: auto',
   the standard properties reset to 'initial', and an explicit
   'scrollbar-color' - and the gutter stayed empty in all four, because
   headless Chromium draws an overlay scrollbar that is only painted while it
   is being used. A screenshot can therefore never show a thumb here whatever
   this sheet says, so the affordance has to be drawn by the screen.
   The fade also cannot live on the scroller's own background: the cards are
   opaque and sit on top of it. It is on the body, over everything. */
.hm-ng .hs-screen__body::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 34px;
  z-index: 3;
  pointer-events: none;
  background-image: linear-gradient(0deg,
    rgba(8,6,3,0.86) 0%, rgba(8,6,3,0.52) 42%, rgba(8,6,3,0.20) 72%, rgba(8,6,3,0) 100%);
}

/* Centred rather than hung from the top edge: what did not fill the frame used
   to pile all of its slack at the bottom, and 280 rows of stone under the last
   card is what a screen that has run out looks like. */
/* The keyword "safe" matters: the content of this screen is taller than 768 with
   a long key list, and a plain centred flex item that overflows overflows in BOTH
   directions, so the top of the column goes above the frame where nothing can
   scroll it back. A safe centre centres when it fits and falls back to start
   when it does not. */
.hm-ng .hs-screen__body { align-items: center; align-items: safe center; }
/* ---- three columns wherever three columns fit, and one row per row.
   The kit's grid is two columns, and with five cards in it the auto-placer left
   two whole cells empty at 1366x768: Difficulty sat beside nothing, Victory
   took a row of its own because it spans, and Seed sat beside nothing again.
   Measured on the shot, the column wanted 857px inside a 697px frame - 160px
   of overflow, which is why the SEED panel was cut through the middle at the
   bottom edge of the viewport - and 236px of that overflow was the two empty
   cells' rows. Three columns of ~380px inside this frame put Map size,
   Opponents and Difficulty in one row and Seed beside Victory in the next.
   The DOM order does the rest: Seed is written before Victory precisely so
   that the spanning card is last and no cell is skipped over at either
   breakpoint. */
@media (min-width: 1100px) {
  .hm-ng .hs-setup__grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .hm-ng__victory { grid-column: span 2; }
}
@media (max-width: 1099px) {
  .hm-ng__victory { grid-column: 1 / -1; }
}

/* ---- twelve paintings, one light.
   Side by side the twelve town screens read as a scraped folder rather than as
   a set: Frostmark is grey-white, Saltreach a beige sunset, Prismgate a purple aurora,
   Gravewatch a pale grey. They were authored as twelve separate paintings and
   nothing had ever put them in one frame. One grade and one sky wash over all
   twelve is what makes them a set, and the selected tile keeps the grade rather
   than dropping it - a tile that jumps out of the palette when you pick it is
   the same defect from the other end. */
.hm-ng .hs-faction__bg {
  filter: brightness(0.84) saturate(0.52) contrast(1.06) sepia(0.24);
}
.hm-ng .hs-faction--on .hs-faction__bg {
  filter: brightness(1.02) saturate(0.68) contrast(1.06) sepia(0.14);
}
.hm-ng .hs-faction__scene::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(180deg,
    rgba(255,206,140,0.22) 0%,
    rgba(255,190,120,0.07) 30%,
    rgba(12,10,16,0.10) 62%,
    rgba(6,5,10,0.38) 100%);
}
/* One frame, too: the same gilt keyline every plate on this build carries. */
.hm-ng .hs-faction {
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.92), inset 0 0 0 2px rgba(216,178,96,0.26);
}
.hm-ng .hs-faction--on {
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.92), inset 0 0 0 2px #d9b25a,
    0 0 16px rgba(217,178,90,0.45);
}

/* Twelve factions in two rows of six, at every width. auto-fill packed eight
   into the first row at 1920 and left four cells of bare wood beside the second,
   which reads as a grid that ran out rather than as a set of twelve. */
/* The tile is exactly as wide as the painting inside it. Left to stretch, a
   255px tile framed a 160px painting in two black bars, which is the one thing
   a picker of paintings must not do. 200 x 112 is the town screen at an exact
   1/6.4 - the same aspect, whole rows. */
.hm-ng .hs-factiongrid {
  grid-template-columns: repeat(6, 200px);
  justify-content: center;
}
.hm-ng .hs-faction__scene { height: 112px; }
/* ---- and it has to use the room when there is room.
   The same stack that overflows at 768 underflowed at 1080 with 179px of bare
   grass under its last card: one fixed layout, wrong at both ends. The faction
   paintings are what the extra height is for - the screen's whole argument is
   that a faction is chosen by looking at it - so above 1000 rows they are drawn
   at 1/5 of the town screen rather than 1/6.4.
   This block used to sit ABOVE the two unconditional rules it overrides, and a
   media query carries no specificity of its own, so both of its tile rules lost
   on document order and the block did nothing at all: shot at 1920x1080 the
   tiles measured 200px wide with a 112px scene, which are the unconditional
   values. Moved below them it applies, the grid spans 1530 of the panel's 1540
   usable pixels instead of 1230, and the 165px of bare card down each side of
   the picker - which measured as flat surface on the blank-surface scan - is
   the painting instead. */
@media (min-width: 1700px) and (min-height: 1000px) {
  .hm-ng .hs-factiongrid { grid-template-columns: repeat(6, 250px); }
  .hm-ng .hs-faction__scene { height: 140px; }
  .hm-ng .hs-setup { gap: 18px; }
}

/* Twelve in two rows at 1366 as well, at 3/4 the tile. Four across was three
   rows, and the third row plus its captions is 140px of the 365px this screen
   overflows by at 768 - a row of thumbnails is the cheapest height on the
   screen to give back, and giving it back is worth more than 50px of thumbnail. */
@media (max-width: 1400px) {
  /* 180, not 150: at 150 the six tiles spanned 930 of the 1160 usable pixels
     inside the picker and left 115px of bare card down each side, which the
     blank-surface scan counts and the eye reads as a grid that has run out.
     The height is unchanged - the extra width is a wider window onto the same
     painting, and this breakpoint is 365px short before it is a pixel wide. */
  .hm-ng .hs-factiongrid { grid-template-columns: repeat(6, 180px); }
  .hm-ng .hs-faction__scene { height: 84px; }
  .hm-ng .hs-faction__cap { font-size: max(10px, var(--hm-type-floor)); }
}

/* A choice is as wide as its label. They stretched to fill the row, so "On" and
   "Off" were 340px each on the Settings screen and "Small" was 130px here: a
   button whose width says nothing about what it does. */
.hm-ng .hs-choices { flex-wrap: wrap; }
.hm-ng .hs-choice { flex: 0 0 auto; min-width: 92px; }
/* Three columns at 1366 leaves each card 381px wide, and 92px floors put four
   choices at 386px in a 357px row - so Impossible and Extra Large each wrapped
   alone onto a second line, which reads as a row that ran out rather than as a
   set of four. 70px is still wider than the widest label needs and lets all
   four sit in one row. */
@media (max-width: 1500px) {
  .hm-ng .hs-choice { min-width: 70px; }
}

/* ---- the two plates. Large, gold-bezelled, the lettering in smooth
   bevelled gold serif: the reference's BEGIN and BACK. */
.hm-ng__plates {
  display: flex;
  justify-content: flex-end;
  gap: 14px;
  margin: 12px 0 2px;
}
.hm-ng__plate {
  width: 236px;
  height: 56px;
  padding: 0;
  gap: 0;
  background-color: #6e4e1f;
  background-image:
    radial-gradient(60% 80% at 30% 20%, rgba(255,236,190,0.22) 0%, rgba(255,236,190,0) 66%),
    linear-gradient(180deg, #8d6a2c 0%, #6b4d1c 46%, #47320f 100%);
  box-shadow:
    inset 0 0 0 1px #0a0704, inset 0 0 0 2px #e9cf92, inset 0 0 0 4px #b8903a, inset 0 0 0 5px #5d4212, inset 0 0 0 6px #0a0704,
    0 4px 10px rgba(0,0,0,0.6);
}
.hm-ng__plate::before, .hm-ng__plate::after { display: none; }
.hm-ng__plate--begin {
  background-color: #b9932f;
  background-image:
    radial-gradient(60% 80% at 30% 20%, rgba(255,246,210,0.30) 0%, rgba(255,246,210,0) 66%),
    linear-gradient(180deg, #f3dd9a 0%, #d9b356 34%, #a97f1c 70%, #6d5210 100%);
}
.hm-ng__plategold {
  font-family: "C059", "Nimbus Roman", "Liberation Serif", "Times New Roman", Georgia, serif;
  font-weight: 700;
  font-size: 30px;
  line-height: 1;
  letter-spacing: 0.06em;
  color: #ffe9b6;
  background-image: linear-gradient(178deg, #fffdf2 0%, #ffeeb8 30%, #e8c163 52%, #b8842a 70%, #fff0c4 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter:
    drop-shadow(1px 0 0 #1a1004) drop-shadow(-1px 0 0 #1a1004)
    drop-shadow(0 1px 0 #1a1004) drop-shadow(0 -1px 0 #1a1004)
    drop-shadow(0 2px 0 #3a2608) drop-shadow(0 3px 4px rgba(0,0,0,0.7));
}
.hm-ng__plate:active:not(:disabled) .hm-ng__plategold { transform: translateY(1px); }

/* ---- the panel: blue leather in a thick gold bezel, the reference's
   scenario chooser (expansion-gallery_hc_scr_4), with every card inside it
   bevelled in gilt rather than ruled with a hairline. */
.hm-ng .hs-setup {
  padding: 14px 14px 12px;
  background-color: #1c2a4e;
  background-image:
    radial-gradient(70% 60% at 20% 0%, rgba(120,150,220,0.25) 0%, rgba(0,0,0,0) 60%),
    radial-gradient(50% 50% at 85% 90%, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 70%),
    repeating-linear-gradient(97deg, rgba(200,220,255,0.05) 0 1px, rgba(0,0,0,0) 1px 5px),
    repeating-linear-gradient(13deg, rgba(0,0,0,0.12) 0 1px, rgba(0,0,0,0) 1px 7px),
    linear-gradient(180deg, #27386a 0%, #1c2a4e 50%, #121b36 100%);
  box-shadow:
    inset 0 0 0 1px #0a0704, inset 0 0 0 2px #e9cf92, inset 0 0 0 5px #b8903a, inset 0 0 0 6px #5d4212, inset 0 0 0 7px #0a0704,
    inset 0 0 40px rgba(0,0,0,0.45), 0 12px 40px rgba(0,0,0,0.8);
}
.hm-ng .hs-setup .hs-card.hs-wood {
  box-shadow:
    inset 0 0 0 1px #0a0704, inset 0 0 0 2px #c9a24a, inset 0 0 0 3px #5d4212, inset 0 0 0 4px rgba(0,0,0,0.6),
    inset 0 5px 0 4px rgba(255,230,180,0.07), inset 0 -6px 0 4px rgba(0,0,0,0.28), 0 2px 6px rgba(0,0,0,0.5);
}

.hm-ng__note { color: #2c2013; }
.hm-ng__note .hs-card__title { color: #43301a; }
.hm-ng__note .hs-muted { color: #5a4527; }

/* ============================================================== the phone
   Written last so it wins on document order over every rule above, the way
   the 1700px block had to be moved below the tile rules it overrides. None of
   this is a media query: the class is set from usePhoneLayout, which is the
   same test every other screen on this build uses, so "phone" means one thing
   across the app rather than one breakpoint here and another there.

   The desktop picker is six fixed 200px columns. That is 1200px of grid before
   gaps, and it does not become a phone layout by shrinking - at 390 it has to
   be a different number of columns. auto-fill with a floor lets the same
   rule serve a 390 portrait (2 columns) and an 844 landscape (4), and the
   paintings keep their aspect because the tile height follows the width. */
.hm-ng--phone .hs-setup { width: 100%; }
.hm-ng--phone .hs-setup.hs-scroll { overflow: visible; }
.hm-ng--phone .hs-factiongrid {
  grid-template-columns: repeat(auto-fill, minmax(116px, 1fr));
  justify-content: stretch;
  gap: 6px;
}
/* 1/6.4 of the town screen, as the desktop tile is - held by ratio rather than
   by a pixel height, because the column width is now fluid. */
.hm-ng--phone .hs-faction__scene { height: auto; aspect-ratio: 200 / 112; }
.hm-ng--phone .hs-faction__cap { font-size: max(11px, var(--hm-type-floor)); padding: 2px 4px; }

/* One card per row. The kit's grid is two or three columns of ~380px, and two
   of those side by side at 390 gave every option card a 161px column - which
   is where "Small 48 x 48" wrapped to three lines and the choices spilled. */
.hm-ng--phone .hs-setup__grid { grid-template-columns: 1fr; gap: 8px; }
.hm-ng--phone .hm-ng__victory { grid-column: 1 / -1; }
.hm-ng--phone .hs-card { padding: 8px 10px; }
.hm-ng--phone .hs-choices { flex-wrap: wrap; gap: 6px; }
/* A finger, not a pointer: 44px is the smallest target that is reliably hit. */
.hm-ng--phone .hs-choice { min-height: 44px; padding: 6px 10px; }
/* The long-form explainer is desktop reading. On a phone it is 200 words
   between the player and the button that starts the game. */
.hm-ng--phone .hm-ng__note { display: none; }

/* Three columns in portrait rather than two. The paintings are the point of
   this screen, so they stay as large as the width allows - but at two columns
   the fourteen of them were seven rows deep, and every option and the button
   that starts the game sat under all of it. Three columns is five rows. */
@media (orientation: portrait) {
  .hm-ng--phone .hs-factiongrid { grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); }
}

/* BEGIN and BACK are 236px each on the desktop plate - 486px of row justified
   to the right, which on a 390 screen put BEGIN at x -118 with nothing able to
   reach it. Two equal halves of whatever there is, and STUCK to the bottom of
   the scroller: a player who has scrolled to the paintings should not have to
   scroll back past every option to start the game. */
.hm-ng--phone .hm-ng__plates {
  position: sticky;
  bottom: 0;
  z-index: 3;
  justify-content: stretch;
  gap: 8px;
  margin: 10px 0 0;
  padding: 8px 0 calc(8px + env(safe-area-inset-bottom, 0px));
  background: linear-gradient(180deg, rgba(20,14,8,0) 0%, rgba(20,14,8,0.92) 28%, rgba(20,14,8,0.97) 100%);
}
.hm-ng--phone .hm-ng__plate { width: auto; flex: 1 1 0; height: 52px; }
/* A sticky bar sits ON the content behind it, so the last card has to be able
   to scroll clear of it - without this the Opponents title was under the
   plates at the bottom of the scroll and could not be read at all. */
.hm-ng--phone .hs-setup { padding-bottom: 76px; }

/* The summary beside the title is the whole setup in one line - it does not
   fit beside NEW GAME at 390 and was cut off mid-word at the right edge. It
   says nothing the cards below do not, so on a phone it goes. */
.hm-ng--phone .hs-screen__sub { display: none; }
`;

/** Mounts this screen's sheet once, and always after the kit's. */
function SetupStyles(): null {
  useStyleSheet(SETUP_STYLE_ID, SETUP_CSS, StyleLayer.screen);
  return null;
}
